import React, { useEffect, useRef } from "react";
import BotProfile from "../assets/bot-profile.png";
import JordanBotProfile from "../assets/jordan-bot.png";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface Message {
  sender: "user" | "bot";
  text: string;
}

interface ChatComponentProps {
  messages: Message[];
  streamingMessage?: Message | null; // Make it optional
  botProfileImage?: string;
  isTranscriptionOn: boolean;
  onTranscriptionToggle: () => void;
  handleMetadataDownload: () => void;
  handleSummaryDownload: () => void;
  isLoading: boolean;
  isMetadataLoading: boolean;
  selectedPrompt: string;
  isPlaying: boolean;
  isShowDownloadButton: boolean;
  selectedVoice: string;
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  messages,
  streamingMessage,
  isTranscriptionOn,
  onTranscriptionToggle,
  handleMetadataDownload,
  handleSummaryDownload,
  isLoading,
  isMetadataLoading,
  selectedPrompt,
  isPlaying,
  isShowDownloadButton,
  selectedVoice
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = React.useState(false);
  const [selectedDownloadOption, setSelectedDownloadOption] = React.useState("Conversation Transcript");

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDownloadDropdownOpen && 
          downloadDropdownRef.current && 
          !downloadDropdownRef.current.contains(event.target as Node)) {
        setIsDownloadDropdownOpen(false);
      }
    };

    // Only add listener if dropdown is open
    if (isDownloadDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDownloadDropdownOpen]);

  const toggleDownloadDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
  };

  const getBotProfileImage = () => {
    switch (selectedVoice) {
      case "Jordan":
        return JordanBotProfile;
      case "Merrisa":
      default:
        return BotProfile;
    }
  };

  const typingAnimationStyle = {
    '@keyframes highlight': {
      '0%': { backgroundColor: 'rgba(59, 130, 246, 0.1)' },
      '100%': { backgroundColor: 'transparent' }
    },
    animation: 'highlight 1.5s ease-out'
  };

  return (
    <div className="flex flex-col h-[calc(80vh-24rem)] md:h-[calc(100vh-6rem)] mt-0  mb-0  rounded-2xl mx-4 md:mb-4 md:ml-0 md:mt-0 md:mx-2 bg-white p-0 md:p-2 shadow-lg border border-gray-200">
      {/* in the above line mx-2 is given for adding the margin between the chat component*/}
      <div className="hidden md:flex justify-between items-center p-2 bg-white rounded-t-lg">
        {/* Transcription Toggle - only visible on desktop */}
        <div className="flex items-center space-x-1 -mt-2 -ml-1">
          <span className="text-xs font-medium md:text-base md:font-semibold md:leading-[22px] md:tracking-[0.25px]">
            Transcription:
          </span>
          <button
            onClick={onTranscriptionToggle}
            className={`w-9 md:w-12 h-5 md:h-6 flex items-center rounded-full p-0.5 transition-colors duration-300 ${
              isTranscriptionOn ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            <div
              className={`h-3 md:h-4 w-3 md:w-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                isTranscriptionOn ? "translate-x-5 md:translate-x-6" : "translate-x-0"
              }`}
            ></div>
          </button>
        </div>

        {/* Download Dropdown - only visible on desktop */}
        <div ref={downloadDropdownRef} className="relative">
          <button
            onClick={toggleDownloadDropdown}
            disabled={isPlaying || !isShowDownloadButton}
            className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px]
              ${(isPlaying || !isShowDownloadButton)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'text-white bg-blue-500 hover:bg-blue-600'} 
              focus:outline-none`}
          >
            Download
            <ChevronDownIcon className="ml-1 h-4 w-4" />
          </button>

          {isDownloadDropdownOpen && !isPlaying && isShowDownloadButton && (
            <div className="absolute right-0 w-[170px] rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
              <div className="px-4 py-2 text-sm font-semibold leading-[22px] tracking-[0.25px] text-gray-700">
                Choose the Format
              </div>
              <div className="px-4 py-2">
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="radio"
                    name="downloadOption"
                    value="Conversation Transcript"
                    checked={selectedDownloadOption === "Conversation Transcript"}
                    onChange={() => setSelectedDownloadOption("Conversation Transcript")}
                    className="form-radio text-blue-500"
                  />
                  <span className="text-base font-semibold leading-[22px] tracking-[0.25px]">Conversation Transcript</span>
                </label>
                {/* <label className="flex items-center space-x-2 mt-2 cursor-pointer hover:bg-gray-50 p-2 rounded">  
                  <input
                    type="radio"
                    name="downloadOption"
                    value="Call Transcript"
                    checked={selectedDownloadOption === "Call Transcript"}
                    onChange={() => setSelectedDownloadOption("Call Transcript")}
                    className="form-radio text-blue-500"
                  />
                  <span className="text-base font-semibold leading-[22px] tracking-[0.25px] ">
                    Data Extract
                  </span>
                </label> */}
              </div>
              <div className="px-4 py-2 ">
                <button
                  onClick={() => {
                    if (selectedDownloadOption === "Conversation Transcript") {
                    handleMetadataDownload();
                    } else {
                      handleSummaryDownload();
                    }
                    setIsDownloadDropdownOpen(false);
                  }}
                  disabled={isPlaying || !isShowDownloadButton}
                  className={`w-full py-2 px-4 text-sm font-medium text-center rounded-lg
                    ${(isPlaying || !isShowDownloadButton)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-white bg-blue-500 hover:bg-blue-600'}`}
                >
                  {isMetadataLoading || isLoading ? 'Downloading...' : 'Download'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-2 md:p-4 rounded-md  scrollbar-thin scrollbar-thumb-gray-300"
      >
        {/* Render completed messages */}
        {messages.map((message, index) => {
          // Check if this is the last bot message and is currently being updated
          const isActiveMessage = 
            message.sender === "bot" && 
            index === messages.length - 1 && 
            messages.length > 0;
            
          return (
            <div
              key={index}
              className={`my-1 md:my-2 flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              {message.sender === "bot" && (
                <img
                  src={getBotProfileImage()}
                  alt="Bot"
                  className="w-6 h-6 md:w-8 md:h-8 rounded-full mr-2 object-cover shrink-0"
                  loading="lazy"
                />
              )}
              <div
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg max-w-[90%] md:max-w-[75%] break-words ${
                  message.sender === "user" ? "bg-[#D8EAFF] text-black" : "bg-gray-200 text-black"
                } ${isActiveMessage ? "typing-message" : ""}`}
              >
                <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">
                  {message.text}
                </p>
              </div>
            </div>
          );
        })}
        
        {/* Render currently streaming message if it exists */}
        {streamingMessage && (
          <div className={`my-1 md:my-2 flex ${streamingMessage.sender === "user" ? "justify-end" : "justify-start"}`}>
            {streamingMessage.sender === "bot" && (
              <img
                src={getBotProfileImage()}
                alt="Bot"
                className="w-6 h-6 md:w-8 md:h-8 rounded-full mr-2 object-cover shrink-0"
                loading="lazy"
              />
            )}
            <div
              className={`px-3 py-2 md:px-4 md:py-2 rounded-lg max-w-[90%] md:max-w-[75%] break-words ${
                streamingMessage.sender === "user" ? "bg-[#D8EAFF] text-black" : "bg-gray-200 text-black"
              }`}
            >
              <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">
                {streamingMessage.text}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatComponent;
